/**
 * Assistant drawer state (#765, spec §8.0; model:
 * `docs/plans/2026-09-21-assistant-subject-model.md`): whether the panel is
 * open and what it is ABOUT. One provider per layout (standalone and
 * embedded); every entry point — the record header, the app header — calls
 * `openAssistant` with a topic, and the same drawer opens.
 *
 * A topic is a subject plus the name shown for it. The name is not decoration:
 * the panel is non-modal, so the user keeps browsing with it open, and an
 * assistant that will not say which record it is answering about can be wrong
 * without anyone noticing (#922 — the panel kept answering about the record it
 * was opened on). Naming the topic is what makes every other rule here safe.
 *
 * Mounted INSIDE `AssistantHostProvider` in both layouts, so this provider
 * reads the record on screen itself. That is deliberate: the topic has exactly
 * one decider. An earlier attempt had the drawer push the record in from
 * outside, which split the decision in two.
 *
 * What moves the topic while the panel is open:
 * - the record on screen changes → a topic about a RECORD follows it; a topic
 *   about a deal (lead, contact, company) follows the deal of the record on
 *   screen — the level the user chose holds, the subject within it moves
 *   (R4, #765 Ф2); a record with no deal leaves it where it is, marked;
 * - a paid turn is in flight → the topic FREEZES until it settles. Moving it
 *   bumps the generation in `useAssistant` and abandons the turn being polled,
 *   which `AssistantSession` is mounted outside the sheet precisely to prevent
 *   (Codex P2 round 23). Every case that freezes ends by itself — an answer, a
 *   refusal, a deadline — so the hold is always temporary; a condition without
 *   such an end must never be added here, or following stops for good;
 * - no record on screen (a dashboard, a record still loading, a record that
 *   failed to open) → the topic STAYS and is marked `offScreen`. `host` is
 *   null in all three of those cases, so freezing costs a badge, while
 *   clearing would empty a working panel and pinning would strand it forever.
 *
 * A close keeps the topic rather than clearing it, so an in-flight turn keeps
 * polling through the hook and the conversation is still there on return. It
 * is not a promise about WHICH record reopens: every entry point opens with
 * its own, and the follow above then applies the screen. It does
 * not survive an organization change: the provider closes the drawer and
 * drops the topic itself, because the drawer may be unmounted by a feature
 * flag before any cleanup of its own could run (Codex P2, rounds 13 and 21).
 *
 * What the subject's access lets it ask is decided here too (E1.5, #1645,
 * #1677), once for every entry point and for the follow above:
 * - a grant without the record or its text (`canAskAboutRecord` — the
 *   «Аналитик» preset) may ask about no record and no deal: the screen
 *   projects nothing onto either level (`screenTopic`/`screenEntityTopic` are
 *   `null`, `recordRefused` says why), so no entry point offers one and the
 *   topic never follows or is offered onto one;
 * - a subject that may ask neither about a record nor about the whole base
 *   has no assistant (`available`) — no entry point, not a disabled one;
 * - a topic the access refuses when `/access/me` answers (or a refetch
 *   changes the grant) is left: the whole base moves to the record on screen
 *   or closes (review #1678); a record or a deal closes — unless a turn about
 *   it is in flight, which stays readable to the end, with no new question
 *   (the panel's composer closes on `topicRefused`).
 *
 * Until `/access/me` answers the role decides, as everywhere in E1.5: the
 * entry points show as before the grant, and the rule above closes whatever
 * was opened in that moment.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { buildSubjectKey, type AssistantSubject } from '@aica/api-types/schemas';
import { canAskAboutRecord } from '@/lib/route-permissions';
import { useOrgTopicState } from '@/hooks/assistant/useOrgTopic';
import { useAssistantHost } from './AssistantHostContext';
import { useScreenSubject } from './ScreenSubjectContext';

/** A subject plus what to call it on screen. The name never reaches a request. */
export interface AssistantTopic {
  subject: AssistantSubject;
  label: string;
}

/** The record on screen as a topic. */
export interface AssistantScreenTopic extends AssistantTopic {
  subject: Extract<AssistantSubject, { scope: 'record' }>;
}

/** The deal (lead, contact, company) of the record on screen as a topic (#765 Ф2). */
export interface AssistantScreenEntityTopic extends AssistantTopic {
  subject: Extract<AssistantSubject, { scope: 'entity' }>;
}

/** What the screen projects onto each level (R2 of the subject model). */
interface ScreenProjection {
  record: AssistantScreenTopic | null;
  entity: AssistantScreenEntityTopic | null;
}

export interface AssistantDrawerContextValue {
  open: boolean;
  topic: AssistantTopic | null;
  /**
   * The record on screen as a topic, or `null` on a screen without one — or
   * for a subject whose access may not ask about a record (`recordRefused`).
   */
  screenTopic: AssistantScreenTopic | null;
  /**
   * The deal of the record on screen, or `null` — no record, it has none, or
   * the access may not ask about it (`recordRefused`).
   */
  screenEntityTopic: AssistantScreenEntityTopic | null;
  /**
   * The subject's access refuses every question about a record and its deal
   * (a grant without `records.read` and `evidence.text`, #1677) — whatever
   * the screen. Said as the reason, before «open a record».
   */
  recordRefused: boolean;
  /**
   * The topic shown is one the access refuses — held only while a turn about
   * it is in flight and after it, until the panel is closed. No new question.
   */
  topicRefused: boolean;
  /**
   * There is something to open: a record (on this screen or another), the
   * whole base, or a turn in flight to return to. `false` — no entry point
   * renders, rather than one that is disabled for good.
   */
  available: boolean;
  /**
   * What «ask about this instead» offers when the topic is off screen (R8):
   * the screen at the topic's own level, or — when the screen has nothing at
   * that level, a record without a deal — the record itself. `null` when
   * there is nothing to offer.
   */
  offerTopic: AssistantTopic | null;
  /**
   * The topic is not what the screen shows — the user walked to another
   * record while a turn was in flight, or left records altogether. The panel
   * says so and offers to switch; it never switches behind the user's back.
   */
  offScreen: boolean;
  /**
   * A paid turn is in flight (reported by the session). The topic cannot move
   * while it is, and a control that would move it says so instead of trying.
   */
  busy: boolean;
  /**
   * Opens the panel on `topic`. While a paid turn is in flight it only OPENS
   * the panel, on the conversation that is running: moving the subject would
   * bump the session's generation and abandon the turn being polled (R6).
   * That is decided here, once, for every entry point — the record button,
   * the deal chip, the sidebar, the scope switcher (#1041 review). The one
   * deliberate way out is `{ evenIfBusy: true }`, which only the offer line
   * passes: it is the explicit «ask about this instead» (R8).
   */
  openAssistant: (topic: AssistantTopic, options?: { evenIfBusy?: boolean }) => void;
  closeAssistant: () => void;
  /**
   * The session reports that a paid turn is in flight. Owned here because the
   * topic is decided here: "do not move while something is at risk" is part of
   * resolving the topic, not a concern of the panel.
   */
  reportBusy: (busy: boolean) => void;
}

/**
 * Set on `<body>` while the panel is open. `styles/chat-widget.css` lifts the
 * Bitrix24 support widget above the sheet only in this state, so the customer
 * can still write to us next to an open assistant (§8.0, D14).
 */
export const ASSISTANT_OPEN_CLASS = 'assistant-open';

const AssistantDrawerContext = createContext<AssistantDrawerContextValue | null>(null);

/**
 * The screen at the topic's own level — what the topic should equal when
 * nothing holds it. `org` does not depend on the screen at all (Ф3).
 */
function projectOnto(
  topic: AssistantTopic,
  screen: ScreenProjection
): AssistantTopic | null {
  switch (topic.subject.scope) {
    case 'record':
      return screen.record;
    case 'entity':
      return screen.entity;
    default:
      return topic;
  }
}

/**
 * The topic the screen calls for, given the one being shown. Pure, and used
 * both to render and to persist — one rule, so the two can never disagree.
 *
 * The level never changes here: a topic the user widened to a deal is their
 * own choice, and the record under the cursor must not silently narrow it
 * back to one call; a record topic is never widened either (R3, R4). Only the
 * subject WITHIN the level follows the screen, and an empty projection — no
 * record, or a record without a deal — leaves it where it is (R5).
 */
function followScreen(
  current: AssistantTopic | null,
  screen: ScreenProjection
): AssistantTopic | null {
  if (!current) return current;
  const projected = projectOnto(current, screen);
  if (!projected || projected === current) return current;
  if (buildSubjectKey(projected.subject) !== buildSubjectKey(current.subject)) {
    return projected;
  }
  // Same subject, renamed (the header resolves a name late): keep the very
  // same subject object so the panel and the session hook see no change. The
  // host re-registers on its own changes too (a tab switch, audio becoming
  // ready), and churning the subject for a subject that never changed would
  // remount the panel and re-read history.
  return current.label === projected.label
    ? current
    : { subject: current.subject, label: projected.label };
}

export function AssistantDrawerProvider({
  children,
  selectedOrgId,
  enabled,
}: {
  children: ReactNode;
  /** The organization the topic belongs to; a change closes the drawer. */
  selectedOrgId: string | null;
  /**
   * The one place availability is decided — the runtime flag in the
   * standalone app, the session's `features.assistant` in the iframe. Off,
   * the context is `null`: no entry point renders and the drawer renders
   * nothing, without anyone reading the flag again (owner's review, 17.09).
   */
  enabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  // What the user last CHOSE, plus whatever following has persisted on top
  // of it. What the panel shows is `topic` below, resolved from this.
  const [chosenTopic, setChosenTopic] = useState<AssistantTopic | null>(null);
  const [busy, setBusy] = useState(false);
  const host = useAssistantHost();
  // The subject of either shell — the portal's PermissionProvider, or the
  // iframe's `EmbeddedScreenSubjectProvider`; both are mounted above this.
  const recordRefused = !canAskAboutRecord(useScreenSubject());
  const { topic: orgTopic, refused: orgRefused } = useOrgTopicState();

  // A refused record is projected as no record at all: following, the offer
  // and every entry point read these two, so none can land on it.
  const hostRecordId = recordRefused ? null : (host?.recordId ?? null);
  const hostLabel = host?.label ?? '';
  const screenTopic = useMemo<AssistantScreenTopic | null>(
    () =>
      hostRecordId === null
        ? null
        : { subject: { scope: 'record', recordId: hostRecordId }, label: hostLabel },
    [hostRecordId, hostLabel]
  );
  // Memoized on the host's entity object, not on the host: the host is
  // re-registered on a tab switch or when audio decodes, while the bridge
  // keeps the entity object itself stable until the entity changes.
  const hostEntity = recordRefused ? null : (host?.entity ?? null);
  const screenEntityTopic = useMemo<AssistantScreenEntityTopic | null>(
    () =>
      hostEntity === null
        ? null
        : {
            subject: {
              scope: 'entity',
              entityType: hostEntity.entityType,
              entityId: hostEntity.entityId,
              installationId: hostEntity.installationId,
            },
            label: hostEntity.label,
          },
    [hostEntity]
  );
  const screen = useMemo<ScreenProjection>(
    () => ({ record: screenTopic, entity: screenEntityTopic }),
    [screenTopic, screenEntityTopic]
  );

  // Read through a ref: `openAssistant` stays one stable function for every
  // entry point, and the check happens at click time, not at render time.
  const busyRef = useRef(false);
  const openAssistant = useCallback(
    (next: AssistantTopic, options?: { evenIfBusy?: boolean }) => {
      if (!busyRef.current || options?.evenIfBusy) setChosenTopic(next);
      setOpen(true);
    },
    []
  );

  const closeAssistant = useCallback(() => setOpen(false), []);

  const reportBusy = useCallback((next: boolean) => {
    busyRef.current = next;
    setBusy(next);
  }, []);

  // The topic follows the screen while the panel is open and nothing is at
  // risk. Closed, it is the conversation to resume on reopen, and every entry
  // point already opens on the right record — so following there would rewrite
  // a topic the user cannot see.
  const following = open && !busy;
  // Resolved in RENDER, not only in the effect. Deriving it here is what keeps
  // an ordinary navigation from flashing "не на экране": with the move made in
  // an effect alone, the paint between the new record and the effect had the
  // old topic against the new screen — which is exactly the shape of a frozen
  // topic, so the badge and the offer button appeared for a frame on every
  // switch. The effect below still runs, and is not redundant: it PERSISTS
  // what was resolved, so that a freeze starting later holds the record the
  // user was actually on and not the one the panel was opened on.
  const topic = following ? followScreen(chosenTopic, screen) : chosenTopic;
  useEffect(() => {
    if (!following) return;
    setChosenTopic((current) => followScreen(current, screen));
  }, [following, screen]);

  // A topic the subject's access refuses: the whole base to a grant holder
  // (#1645), a record or a deal to a grant without them (#1677).
  const topicRefused =
    topic !== null && (topic.subject.scope === 'org' ? orgRefused : recordRefused);

  // The invariant of the subject model: the topic equals the screen at its
  // own level, or it is marked off screen. There is no third state — but a
  // refused topic is about to be left (below), and «not on screen» would
  // misname a record that is on screen and simply may not be asked about.
  const projection = topic ? projectOnto(topic, screen) : null;
  const offScreen =
    topic !== null &&
    !topicRefused &&
    (projection === null ||
      buildSubjectKey(projection.subject) !== buildSubjectKey(topic.subject));
  const offerTopic = offScreen ? (projection ?? screenTopic) : null;

  // Leaving a refused topic. The whole base moves to the record on screen —
  // or closes — even mid-turn: the API refuses that turn anyway (review
  // #1678). A record or a deal closes, unless a turn about it is in flight:
  // that one is kept readable to the end (a held topic stays until the user
  // closes the panel), and `topicRefused` closes the composer meanwhile.
  const holdRefusedRef = useRef(false);
  const leaveOrgFor = topicRefused && topic?.subject.scope === 'org' ? screenTopic : null;
  useEffect(() => {
    if (!open || !topicRefused) {
      holdRefusedRef.current = false;
      return;
    }
    if (topic?.subject.scope === 'org') {
      if (leaveOrgFor) openAssistant(leaveOrgFor, { evenIfBusy: true });
      else setOpen(false);
      return;
    }
    if (busy) holdRefusedRef.current = true;
    if (holdRefusedRef.current) return;
    setOpen(false);
    setChosenTopic(null);
  }, [open, topicRefused, topic, leaveOrgFor, busy, openAssistant]);

  // Nothing the access lets it ask, and no turn to return to: no assistant
  // here at all. The context stays (the drawer and its session must not
  // unmount under a turn), so this is a flag every entry point reads.
  const available = !recordRefused || orgTopic !== null || busy;

  // The topic belongs to the organization it was opened in: carried into
  // the next tenant it would ask for another organization's history (Codex
  // P2, round 13). Owned here, not by the drawer: the feature flag can
  // unmount the drawer before an effect of its own runs, leaving the panel
  // open on a stale record for the next enabled organization (Codex P2,
  // round 21). The next entry point opens the drawer afresh.
  const orgRef = useRef(selectedOrgId);
  useEffect(() => {
    if (orgRef.current === selectedOrgId) return;
    orgRef.current = selectedOrgId;
    setOpen(false);
    setChosenTopic(null);
    busyRef.current = false;
    setBusy(false);
  }, [selectedOrgId]);

  // Switched off while open: the sheet is gone at once, so the state that
  // says "open" (and the body class with it) must not outlive it.
  useEffect(() => {
    if (enabled) return;
    setOpen(false);
    setChosenTopic(null);
    busyRef.current = false;
    setBusy(false);
  }, [enabled]);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add(ASSISTANT_OPEN_CLASS);
    return () => document.body.classList.remove(ASSISTANT_OPEN_CLASS);
  }, [open]);

  const value = useMemo(
    () => ({
      open,
      topic,
      screenTopic,
      screenEntityTopic,
      recordRefused,
      topicRefused,
      available,
      offerTopic,
      offScreen,
      busy,
      openAssistant,
      closeAssistant,
      reportBusy,
    }),
    [
      open,
      topic,
      screenTopic,
      screenEntityTopic,
      recordRefused,
      topicRefused,
      available,
      offerTopic,
      offScreen,
      busy,
      openAssistant,
      closeAssistant,
      reportBusy,
    ]
  );

  return (
    <AssistantDrawerContext.Provider value={enabled ? value : null}>
      {children}
    </AssistantDrawerContext.Provider>
  );
}

export function useAssistantDrawer(): AssistantDrawerContextValue {
  const context = useContext(AssistantDrawerContext);
  if (!context) {
    throw new Error('useAssistantDrawer must be used within AssistantDrawerProvider');
  }
  return context;
}

/** `null` outside the provider — an entry point simply does not render. */
export function useAssistantDrawerSafe(): AssistantDrawerContextValue | null {
  return useContext(AssistantDrawerContext);
}
